import React, { useState, useEffect } from 'react';
import api from '../services/api';
import { Calculator, Search, ExternalLink, ChevronDown, ChevronUp, AlertTriangle, Loader, BookOpen } from 'lucide-react';
import { toast } from 'react-toastify';

const CATEGORY_COLORS = {
  'External Combustion': { bg: '#EFF6FF', color: '#1E40AF', dot: '#0066FF' },
  'Internal Combustion': { bg: '#FEF3C7', color: '#92400E', dot: '#D97706' },
  'Liquid Storage Tanks': { bg: '#F0FDF4', color: '#15803D', dot: '#16A34A' },
  'Miscellaneous Sources': { bg: '#F5F3FF', color: '#5B21B6', dot: '#7C3AED' },
  'Surface Coating': { bg: '#FFF7ED', color: '#9A3412', dot: '#EA580C' },
  'Mineral Products': { bg: '#F8FAFC', color: '#374151', dot: '#64748b' },
  'Waste Combustion': { bg: '#FEF2F2', color: '#991B1B', dot: '#DC2626' },
  'Food and Agriculture': { bg: '#ECFDF5', color: '#065F46', dot: '#059669' },
};

function FactorRow({ factor }) {
  return (
    <tr style={{ borderTop: '1px solid #F1F5F9' }}>
      <td style={{ padding: '10px 16px', fontWeight: 600, color: '#0A1628', fontSize: 13, whiteSpace: 'nowrap' }}>{factor.pollutant}</td>
      <td style={{ padding: '10px 16px', fontFamily: 'monospace', fontSize: 13, color: '#0066FF', fontWeight: 600 }}>{factor.value}</td>
      <td style={{ padding: '10px 16px', fontSize: 12, color: '#64748b' }}>{factor.unit}</td>
      <td style={{ padding: '10px 16px', fontSize: 12, color: '#374151', lineHeight: 1.55 }}>{factor.notes || ''}</td>
    </tr>
  );
}

function SourceCard({ source }) {
  const [expanded, setExpanded] = useState(false);
  const colors = CATEGORY_COLORS[source.category] || CATEGORY_COLORS['Miscellaneous Sources'];

  return (
    <div style={{ background: 'white', border: '1px solid #E2E8F0', borderRadius: 10, overflow: 'hidden', marginBottom: 10 }}>
      {/* Header */}
      <div onClick={() => setExpanded(!expanded)}
        style={{ padding: '14px 20px', cursor: 'pointer', display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 16 }}>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6, flexWrap: 'wrap' }}>
            <span style={{ background: colors.bg, color: colors.color, fontSize: 11, fontWeight: 700, padding: '2px 8px', borderRadius: 10, whiteSpace: 'nowrap' }}>
              {source.subcategory}
            </span>
            <span style={{ fontSize: 11, color: '#94a3b8' }}>AP-42 Ch. {source.ap42_chapter}</span>
          </div>
          <div style={{ fontFamily: 'var(--font-heading)', fontSize: 15, fontWeight: 700, color: '#0A1628', marginBottom: 3 }}>{source.source_type}</div>
          <div style={{ fontSize: 12, color: '#64748b', display: 'flex', gap: 16, flexWrap: 'wrap' }}>
            <span>Fuel: <strong>{source.fuel}</strong></span>
            <span>Unit: <strong>{source.fuel_unit}</strong></span>
            <span>{source.factors.length} pollutant{source.factors.length !== 1 ? 's' : ''}</span>
          </div>
        </div>
        <div style={{ flexShrink: 0, color: '#94a3b8' }}>
          {expanded ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
        </div>
      </div>

      {/* Expanded content */}
      {expanded && (
        <div style={{ borderTop: '1px solid #F1F5F9' }}>
          {/* Factors table */}
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr style={{ background: '#F8FAFC' }}>
                  <th style={{ padding: '8px 16px', textAlign: 'left', fontSize: 11, fontWeight: 700, color: '#64748b', textTransform: 'uppercase', letterSpacing: 0.5, whiteSpace: 'nowrap' }}>Pollutant</th>
                  <th style={{ padding: '8px 16px', textAlign: 'left', fontSize: 11, fontWeight: 700, color: '#64748b', textTransform: 'uppercase', letterSpacing: 0.5 }}>Factor</th>
                  <th style={{ padding: '8px 16px', textAlign: 'left', fontSize: 11, fontWeight: 700, color: '#64748b', textTransform: 'uppercase', letterSpacing: 0.5 }}>Unit</th>
                  <th style={{ padding: '8px 16px', textAlign: 'left', fontSize: 11, fontWeight: 700, color: '#64748b', textTransform: 'uppercase', letterSpacing: 0.5 }}>Notes / Conditions</th>
                </tr>
              </thead>
              <tbody>
                {source.factors.map((f, i) => <FactorRow key={i} factor={f} />)}
              </tbody>
            </table>
          </div>

          {/* Notes and controls */}
          <div style={{ padding: '14px 20px', borderTop: '1px solid #F1F5F9', display: 'grid', gridTemplateColumns: '1fr', gap: 12 }}>
            {source.notes && (
              <div style={{ background: '#FFFBEB', border: '1px solid #FDE68A', borderRadius: 8, padding: '10px 14px' }}>
                <div style={{ fontSize: 11, fontWeight: 700, color: '#92400E', marginBottom: 4, textTransform: 'uppercase', letterSpacing: 0.5 }}>Notes</div>
                <div style={{ fontSize: 12, color: '#374151', lineHeight: 1.6 }}>{source.notes}</div>
              </div>
            )}
            {source.control_notes && (
              <div style={{ background: '#F0FDF4', border: '1px solid #BBF7D0', borderRadius: 8, padding: '10px 14px' }}>
                <div style={{ fontSize: 11, fontWeight: 700, color: '#15803D', marginBottom: 4, textTransform: 'uppercase', letterSpacing: 0.5 }}>Control Technologies</div>
                <div style={{ fontSize: 12, color: '#374151', lineHeight: 1.6 }}>{source.control_notes}</div>
              </div>
            )}
          </div>

          {/* Footer */}
          <div style={{ padding: '10px 20px', borderTop: '1px solid #F1F5F9', background: '#F8FAFC', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <span style={{ fontSize: 11, color: '#94a3b8' }}>
              AP-42 Chapter {source.ap42_chapter}{source.ap42_table ? ` · Table ${source.ap42_table}` : ''}
            </span>
            <a href={source.ap42_url} target="_blank" rel="noopener noreferrer"
              style={{ fontSize: 12, color: '#0066FF', textDecoration: 'none', display: 'flex', alignItems: 'center', gap: 4, fontWeight: 600 }}>
              <ExternalLink size={12} /> View on EPA.gov
            </a>
          </div>
        </div>
      )}
    </div>
  );
}

function AILookupResult({ result }) {
  return (
    <div style={{ background: 'white', border: '1px solid #E2E8F0', borderRadius: 10, overflow: 'hidden' }}>
      <div style={{ padding: '14px 20px', borderBottom: '1px solid #F1F5F9', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <div>
          <div style={{ fontFamily: 'var(--font-heading)', fontSize: 15, fontWeight: 700, color: '#0A1628' }}>{result.source_identified}</div>
          <div style={{ fontSize: 12, color: '#64748b', marginTop: 2 }}>AP-42 Chapter {result.ap42_chapter} · AI-Assisted Lookup</div>
        </div>
        <span style={{ background: '#FFFBEB', color: '#D97706', fontSize: 11, fontWeight: 700, padding: '3px 10px', borderRadius: 10 }}>
          AI-Assisted
        </span>
      </div>

      <div style={{ overflowX: 'auto' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
          <thead>
            <tr style={{ background: '#F8FAFC' }}>
              <th style={{ padding: '8px 16px', textAlign: 'left', fontSize: 11, fontWeight: 700, color: '#64748b', textTransform: 'uppercase', letterSpacing: 0.5 }}>Pollutant</th>
              <th style={{ padding: '8px 16px', textAlign: 'left', fontSize: 11, fontWeight: 700, color: '#64748b', textTransform: 'uppercase', letterSpacing: 0.5 }}>Factor</th>
              <th style={{ padding: '8px 16px', textAlign: 'left', fontSize: 11, fontWeight: 700, color: '#64748b', textTransform: 'uppercase', letterSpacing: 0.5 }}>Unit</th>
              <th style={{ padding: '8px 16px', textAlign: 'left', fontSize: 11, fontWeight: 700, color: '#64748b', textTransform: 'uppercase', letterSpacing: 0.5 }}>Notes</th>
            </tr>
          </thead>
          <tbody>
            {(result.factors || []).map((f, i) => <FactorRow key={i} factor={f} />)}
          </tbody>
        </table>
      </div>

      <div style={{ padding: '14px 20px', borderTop: '1px solid #F1F5F9', display: 'grid', gridTemplateColumns: '1fr', gap: 12 }}>
        {result.notes && (
          <div style={{ background: '#FFFBEB', border: '1px solid #FDE68A', borderRadius: 8, padding: '10px 14px' }}>
            <div style={{ fontSize: 11, fontWeight: 700, color: '#92400E', marginBottom: 4 }}>Notes</div>
            <div style={{ fontSize: 12, color: '#374151', lineHeight: 1.6 }}>{result.notes}</div>
          </div>
        )}
        {result.control_notes && (
          <div style={{ background: '#F0FDF4', border: '1px solid #BBF7D0', borderRadius: 8, padding: '10px 14px' }}>
            <div style={{ fontSize: 11, fontWeight: 700, color: '#15803D', marginBottom: 4 }}>Control Technologies</div>
            <div style={{ fontSize: 12, color: '#374151', lineHeight: 1.6 }}>{result.control_notes}</div>
          </div>
        )}
        {result.applicable_regulations && result.applicable_regulations.length > 0 && (
          <div style={{ background: '#EFF6FF', border: '1px solid #BFDBFE', borderRadius: 8, padding: '10px 14px', gridColumn: '1 / -1' }}>
            <div style={{ fontSize: 11, fontWeight: 700, color: '#1E40AF', marginBottom: 4 }}>Applicable Regulations</div>
            <div style={{ fontSize: 12, color: '#374151', lineHeight: 1.6 }}>{result.applicable_regulations.join(' · ')}</div>
          </div>
        )}
      </div>

      <div style={{ padding: '10px 20px', borderTop: '1px solid #F1F5F9', background: '#FEF2F2' }}>
        <div style={{ fontSize: 11, color: '#DC2626', display: 'flex', alignItems: 'flex-start', gap: 6 }}>
          <AlertTriangle size={12} style={{ flexShrink: 0, marginTop: 1 }} />
          {result.disclaimer || 'AI-assisted factors. Always verify against current EPA AP-42 tables before use in regulatory submissions.'}
        </div>
      </div>
    </div>
  );
}

export default function EmissionFactors() {
  const [sources, setSources] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [activeCategory, setActiveCategory] = useState('all');
  const [aiQuery, setAiQuery] = useState('');
  const [aiLoading, setAiLoading] = useState(false);
  const [aiResult, setAiResult] = useState(null);
  const [showAIPanel, setShowAIPanel] = useState(false);

  useEffect(() => { loadLibrary(); }, [activeCategory, search]);

  const loadLibrary = async () => {
    try {
      const params = new URLSearchParams();
      if (activeCategory !== 'all') params.append('category', activeCategory);
      if (search) params.append('search', search);
      const res = await api.get(`/emission-factors?${params}`);
      setSources(res.data.sources || []);
      if (res.data.categories) setCategories(res.data.categories);
    } catch (err) {
      toast.error('Failed to load library');
    } finally {
      setLoading(false);
    }
  };

  const handleAILookup = async () => {
    if (!aiQuery.trim()) return toast.error('Describe the source type');
    setAiLoading(true);
    setAiResult(null);
    try {
      const res = await api.post('/emission-factors/ai-lookup', { sourceDescription: aiQuery });
      setAiResult(res.data.result);
    } catch (err) {
      toast.error('AI lookup failed');
    } finally {
      setAiLoading(false);
    }
  };

  return (
    <div>
      <div style={{ marginBottom: 24 }}>
        <div className="page-title" style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <BookOpen size={22} color="#0066FF" />
          Emission Factor Library
        </div>
        <div className="page-subtitle">EPA AP-42 emission factors for air quality permitting and compliance calculations</div>
      </div>

      {/* Search and filter bar */}
      <div style={{ background: 'white', border: '1px solid #E2E8F0', borderRadius: 12, padding: '16px 20px', marginBottom: 20 }}>
        <div style={{ display: 'flex', gap: 12, marginBottom: 14, flexWrap: 'wrap' }}>
          <div style={{ flex: 1, minWidth: 200, position: 'relative' }}>
            <Search size={15} style={{ position: 'absolute', left: 12, top: '50%', transform: 'translateY(-50%)', color: '#94a3b8' }} />
            <input
              style={{ width: '100%', paddingLeft: 36, padding: '9px 12px 9px 36px', border: '1px solid #E2E8F0', borderRadius: 8, fontSize: 13, fontFamily: 'var(--font-body)', outline: 'none', boxSizing: 'border-box' }}
              placeholder="Search by source type, fuel, pollutant, or chapter..."
              value={search}
              onChange={e => setSearch(e.target.value)}
            />
          </div>
          <button
            onClick={() => setShowAIPanel(!showAIPanel)}
            style={{ display: 'flex', alignItems: 'center', gap: 7, padding: '9px 16px', background: showAIPanel ? '#0066FF' : 'white', color: showAIPanel ? 'white' : '#374151', border: '1px solid #E2E8F0', borderRadius: 8, fontSize: 13, fontWeight: 600, cursor: 'pointer', fontFamily: 'var(--font-body)', whiteSpace: 'nowrap' }}>
            <Calculator size={14} />
            AI Lookup
          </button>
        </div>

        {/* Category filters */}
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
          <button onClick={() => setActiveCategory('all')}
            style={{ padding: '5px 12px', borderRadius: 20, fontSize: 12, fontWeight: 600, cursor: 'pointer', fontFamily: 'var(--font-body)', border: activeCategory === 'all' ? 'none' : '1px solid #E2E8F0', background: activeCategory === 'all' ? '#0A1628' : 'white', color: activeCategory === 'all' ? 'white' : '#374151' }}>
            All Sources
          </button>
          {categories.map(cat => {
            const colors = CATEGORY_COLORS[cat] || {};
            return (
              <button key={cat} onClick={() => setActiveCategory(cat)}
                style={{ padding: '5px 12px', borderRadius: 20, fontSize: 12, fontWeight: 600, cursor: 'pointer', fontFamily: 'var(--font-body)', border: activeCategory === cat ? 'none' : '1px solid #E2E8F0', background: activeCategory === cat ? (colors.dot || '#64748b') : 'white', color: activeCategory === cat ? 'white' : '#374151' }}>
                {cat}
              </button>
            );
          })}
        </div>
      </div>

      {/* AI Lookup Panel */}
      {showAIPanel && (
        <div style={{ background: 'white', border: '1px solid #BFDBFE', borderRadius: 12, padding: '20px', marginBottom: 20 }}>
          <div style={{ fontFamily: 'var(--font-heading)', fontSize: 14, fontWeight: 700, color: '#1E40AF', marginBottom: 6, display: 'flex', alignItems: 'center', gap: 8 }}>
            <Calculator size={16} color="#0066FF" /> AI-Assisted AP-42 Lookup
          </div>
          <div style={{ fontSize: 13, color: '#64748b', marginBottom: 14 }}>
            Can't find your source in the library? Describe it and our AI will look up the applicable AP-42 factors. Always verify results against EPA AP-42 before regulatory use.
          </div>
          <div style={{ display: 'flex', gap: 10 }}>
            <input
              style={{ flex: 1, padding: '9px 12px', border: '1px solid #E2E8F0', borderRadius: 8, fontSize: 13, fontFamily: 'var(--font-body)', outline: 'none' }}
              placeholder="e.g. Paint spray booth, asphalt plant drum dryer, medical waste incinerator..."
              value={aiQuery}
              onChange={e => setAiQuery(e.target.value)}
              onKeyDown={e => e.key === 'Enter' && handleAILookup()}
            />
            <button onClick={handleAILookup} disabled={aiLoading}
              style={{ padding: '9px 20px', background: aiLoading ? '#94a3b8' : '#0066FF', color: 'white', border: 'none', borderRadius: 8, fontSize: 13, fontWeight: 600, cursor: aiLoading ? 'not-allowed' : 'pointer', fontFamily: 'var(--font-body)', whiteSpace: 'nowrap', display: 'flex', alignItems: 'center', gap: 7 }}>
              {aiLoading ? <><Loader size={14} style={{ animation: 'spin 0.8s linear infinite' }} />Looking up...</> : 'Look Up Factors'}
            </button>
          </div>
          {aiResult && <div style={{ marginTop: 16 }}><AILookupResult result={aiResult} /></div>}
        </div>
      )}

      <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>

      {/* Results count */}
      <div style={{ fontSize: 13, color: '#64748b', marginBottom: 12 }}>
        {loading ? 'Loading...' : `${sources.length} source${sources.length !== 1 ? 's' : ''} found`}
        {search && <span style={{ marginLeft: 8 }}>for "<strong>{search}</strong>"</span>}
      </div>

      {/* Source cards */}
      {loading ? (
        <div style={{ textAlign: 'center', padding: 48, color: '#64748b' }}>
          <Loader size={32} color="#0066FF" style={{ animation: 'spin 0.8s linear infinite', marginBottom: 12 }} />
          <div>Loading emission factor library...</div>
        </div>
      ) : sources.length === 0 ? (
        <div style={{ background: 'white', border: '1px solid #E2E8F0', borderRadius: 12, padding: 48, textAlign: 'center' }}>
          <BookOpen size={40} color="#E2E8F0" style={{ marginBottom: 16 }} />
          <div style={{ fontFamily: 'var(--font-heading)', fontSize: 16, fontWeight: 700, color: '#0A1628', marginBottom: 8 }}>No sources found</div>
          <div style={{ fontSize: 13, color: '#64748b', marginBottom: 16 }}>Try a different search term or use AI Lookup for unlisted source types.</div>
          <button onClick={() => { setSearch(''); setActiveCategory('all'); }}
            style={{ padding: '8px 16px', background: '#0066FF', color: 'white', border: 'none', borderRadius: 8, fontSize: 13, fontWeight: 600, cursor: 'pointer', fontFamily: 'var(--font-body)' }}>
            Clear Filters
          </button>
        </div>
      ) : (
        sources.map(source => <SourceCard key={source.id} source={source} />)
      )}

      {/* Bottom disclaimer */}
      <div style={{ background: '#F8FAFC', border: '1px solid #E2E8F0', borderRadius: 10, padding: '14px 18px', marginTop: 20 }}>
        <div style={{ fontSize: 12, color: '#64748b', lineHeight: 1.6 }}>
          <strong>Disclaimer:</strong> Emission factors are from EPA AP-42, Compilation of Air Pollutant Emission Factors. Factors represent average conditions and may not reflect site-specific processes. Always verify factors against the current EPA AP-42 document before use in permit applications, compliance demonstrations, or regulatory submissions. Links to EPA AP-42 source documents are provided for each source type.
        </div>
      </div>
    </div>
  );
}
